import type { LocationQuery, RouteLocationGeneric, RouteLocationRaw } from 'vue-router'

/**
 * Stable deep-link contract.
 * Backend emits /l/<intent>?<params>; this map is the ONLY place that knows how
 * intents land on real routes. Map to route NAMES, never paths. Adding an intent:
 * add an entry here + a unit test row + documents/deep-links.adoc.
 */

/** A resolved navigation target, expressed by route NAME (never a path). */
type DeepLinkTarget = { name: string; params?: Record<string, string>; query: LocationQuery }

/**
 * An intent resolver: pure function from inbound query → target, or `null` to
 * trigger the dead-link fallback. Exported for the test seam's `map` parameter.
 */
export type IntentResolver = (query: LocationQuery) => DeepLinkTarget | null

/** The frozen v1 catalog — exported so tests can iterate it exhaustively. */
export type DeepLinkIntent = 'organization' | 'feedback' | 'general-feedback'

const intents = {
  // Passthrough params are spread FIRST so intent-fixed values always win.
  organization: (q) => {
    const orgId = requireParam(q, 'orgId')
    if (!orgId) return null
    return {
      name: 'view-organization',
      params: { id: orgId },
      query: rest(q, ['orgId']),
    }
  },
  feedback: (q) => {
    const messageId = requireParam(q, 'messageId')
    if (!messageId) return null
    return {
      name: 'view-message-feedback',
      params: { id: messageId },
      query: rest(q, ['messageId']),
    }
  },
  // General feedback (bug report / idea / general) has no detail route — it is
  // triaged from the list. The param stays `messageId` (the portal convention is
  // "messageId = the feedback record id", same as `feedback` above); only the
  // `gfb-` prefix distinguishes it. The `tab`/`feedback` target params are
  // ROUTING knowledge, decided here — the backend never sends them.
  'general-feedback': (q) => {
    const feedbackId = requireParam(q, 'messageId')
    if (!feedbackId) return null
    return {
      name: 'feedback',
      query: { ...rest(q, ['messageId']), tab: 'general', feedback: feedbackId },
    }
  },
} satisfies Record<DeepLinkIntent, IntentResolver>

/**
 * Translate a `/l/:intent?<params>` navigation into a real named route.
 *
 * `map` is the test seam: unit tests inject a throwing entry to prove the catch
 * degrades to the fallback. Production callers never pass it.
 *
 * NEVER throws — a broken link must degrade to the fallback, not a router error.
 */
export function resolveDeepLink(
  to: RouteLocationGeneric,
  map: Record<string, IntentResolver> = intents,
): RouteLocationRaw {
  const intent = String(to.params.intent ?? '')
  let target: DeepLinkTarget | null = null
  try {
    // hasOwnProperty guard: without it, prototype keys (`/l/toString`,
    // `/l/constructor`, `/l/__proto__`) resolve to Object.prototype members and
    // return truthy non-route values → Vue Router invalid-redirect error instead
    // of the fallback. URL segments are a system boundary — validate.
    const resolve = Object.prototype.hasOwnProperty.call(map, intent) ? map[intent] : undefined
    target = typeof resolve === 'function' ? (resolve(to.query) ?? null) : null
  } catch (error) {
    // Fallback for the user, diagnostics for us: console.error is emitted so a bug
    // in one entry is distinguishable from malformed inbound links (which log at
    // warn). These are diagnostics only; there is no error-reporting integration here.
    console.error('[deep-links] resolver failed', { intent: safeIntent(intent) }, error)
    target = null
  }
  if (!target) {
    void notifyBrokenLink(intent) // fire-and-forget; see below
    return { name: 'dashboard', params: {}, query: {}, hash: '' }
  }
  // Vue Router merges a named redirect target with the ORIGINAL location's
  // query/params/hash when the target omits them (handleRedirectRecord). Every
  // target sets `query` explicitly (above); `params` and `hash` are normalized
  // here so `params.intent` and inbound fragments never leak into the destination.
  return { params: {}, hash: '', ...target }
}

/**
 * Read a single required query param, trimmed. Returns `null` when the param is
 * absent, empty, whitespace-only, or an array with no non-empty entry. For a
 * repeated param (`?k=a&k=b`) the first non-empty value is used (deterministic).
 *
 * Defensive: never throws — a broken link must degrade to the fallback, not a
 * router error.
 */
export function requireParam(query: LocationQuery, key: string): string | null {
  const raw = query[key]
  const candidates = Array.isArray(raw) ? raw : [raw]
  for (const value of candidates) {
    if (typeof value !== 'string') continue
    const trimmed = value.trim()
    if (trimmed) return trimmed
  }
  return null
}

/**
 * The passthrough: every inbound param NOT consumed by the intent, forwarded to
 * the target unchanged (forward compatibility — the backend can send a new param
 * before the portal knows about it and it still reaches the target view).
 */
export function rest(query: LocationQuery, consumedKeys: string[]): LocationQuery {
  const consumed = new Set(consumedKeys)
  const out: LocationQuery = {}
  for (const key of Object.keys(query)) {
    if (consumed.has(key)) continue
    const value = query[key]
    if (value !== undefined) out[key] = value
  }
  return out
}

/**
 * Telemetry sanitizer: the intent is a user-controlled URL segment (could carry
 * emails/tokens), so logs get the raw value ONLY if it matches `^[a-z-]{1,32}$`;
 * otherwise the bounded token `'invalid'`. Keeps the key diagnostic (which
 * unknown intent is a producer emitting?) without echoing arbitrary input.
 */
export function safeIntent(intent: string): string {
  return /^[a-z-]{1,32}$/.test(intent) ? intent : 'invalid'
}

/**
 * Best-effort dead-link toast. Fires AFTER `router.isReady()` so the `<Sonner>`
 * host is mounted (a toast fired during initial navigation would be lost on a
 * cold email click). Dynamically imports the router to avoid the static cycle
 * `router → intents → router`. Uses `i18n.global.t` — the documented last-resort
 * case (fires outside any component setup context). Its own failures are caught
 * and `console.error`'d with context — never silent, never thrown into navigation.
 */
export async function notifyBrokenLink(intent: string): Promise<void> {
  // Breadcrumb-level: expected user-input noise, distinct from the resolver-defect
  // console.error above.
  console.warn('[deep-links] unresolvable link', { intent: safeIntent(intent) })
  try {
    const { toast } = await import('vue-sonner')
    const { i18n } = await import('@/plugins/i18n')
    const { default: router } = await import('@/router')
    await router.isReady()
    toast.info(i18n.global.t('modules.deepLinks.brokenLink.title'), {
      description: i18n.global.t('modules.deepLinks.brokenLink.description'),
    })
  } catch (error) {
    console.error(
      '[deep-links] failed to notify broken link',
      { intent: safeIntent(intent) },
      error,
    )
  }
}
